import React, { type FC, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { useTheme } from 'styled-components';

import Typography from 'app/ui/components/atoms/typography';
import type { Theme } from 'src/app/providers/theme';

interface CustomTooltipProps {
  tooltipText: string | null;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: string;
  usePortal?: boolean;
}

const TooltipWrapper = styled.div`
  display: inline-flex;
`;

const getTooltipStyle = ({ spacing, colors }: Theme, maxWidth: string) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  padding: spacing(3),
  borderRadius: spacing(2),
  maxWidth,
  backgroundColor: colors.blackSwan,
  zIndex: 999,
});

const CustomTooltip: FC<CustomTooltipProps> = ({
  tooltipText,
  children,
  placement = 'top',
  maxWidth = '500px',
  usePortal = false,
}) => {
  const theme = useTheme();
  const tooltipId = `tooltip-${Math.random().toString(36).substring(2, 9)}`;

  if (!tooltipText) {
    return null;
  }

  const tooltipNode = (
    <Tooltip
      id={tooltipId}
      style={getTooltipStyle(theme, maxWidth)}
      border={`2px solid ${theme.colors.devilsGray}`}
      place={placement}
      opacity={1}
    >
      <Typography variant="caption">{tooltipText}</Typography>
    </Tooltip>
  );

  return (
    <>
      <TooltipWrapper data-tooltip-id={tooltipId}>{children}</TooltipWrapper>
      {usePortal && typeof document !== 'undefined' ? createPortal(tooltipNode, document.body) : tooltipNode}
    </>
  );
};

export default CustomTooltip;
